import { createElement, Fragment } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { ImpactHeroDocument } from "@/components/impactReport/ImpactHeroDocument";
import { ImpactReportPdfBackCover } from "@/components/impactReport/ImpactReportPdfBackCover";
import { ImpactReportPdfCover } from "@/components/impactReport/ImpactReportPdfCover";
import {
  IMPACT_REPORT_ALL_TIME_LABEL,
  IMPACT_REPORT_CERTIFICATION_BADGES,
  IMPACT_REPORT_MASTHEAD_LABEL,
} from "@/constants/impactReport";
import {
  IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PARAM,
  IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PX,
  IMPACT_REPORT_DOCUMENT_FONT_FACE_DESCRIPTORS,
  IMPACT_REPORT_DOCUMENT_FONT_FACES,
  IMPACT_REPORT_DOCUMENT_GEOMETRY_CSS,
  IMPACT_REPORT_DOCUMENT_SHEETS_BEFORE_PAGES,
} from "@/constants/impactReportDocument";
import type { ImpactReportFontDataUris } from "@/interfaces/impactReportAssets";
import type {
  ImpactReportDocumentInput,
  ImpactReportDocumentSource,
} from "@/interfaces/impactReportDocument";
import type { ImpactReportPage } from "@/interfaces/impactReportView";
import { formatDateLong } from "@/utils/date-format";
import {
  resolveImpactReportBrandAssets,
  resolveImpactReportDocumentFonts,
  resolveImpactReportDocumentStyles,
} from "@/utils/impact-report-asset-helper";
import { resolveImpactReportPhotoCredits } from "@/utils/impact-report-helper";

/**
 * ── The impact report as ONE self-contained HTML document ───────────────────────────────────────
 *
 * The converter that turns this into a PDF is a separate service in a separate repo. It receives
 * this string and NOTHING ELSE: no origin to fetch a stylesheet from, no allow-listed font CDN, no
 * JavaScript runtime, and no page geometry of its own. Everything the report needs to look like
 * the report therefore has to be inside the string.
 *
 * THE RULE THIS MODULE IS WRITTEN AROUND: reuse, never re-derive. It renders the SAME components
 * over the SAME page model the screen is already showing, embeds the SAME stylesheet, and calls
 * the SAME photo-credit resolver. There is no second copy of any page's markup here and no page
 * number derived from the payload here — the moment either appears, the PDF is free to disagree
 * with the screen about what the client's report says, and nothing in either half would catch it.
 * The one adjustment it makes is for the cover sheet it adds itself (`withDocumentSheetNumbers`).
 *
 * The PDF's front and back covers are print-only components of their own, in place of the
 * on-screen masthead and colophon strips, which printed alone on otherwise blank sheets. They
 * print the masthead's and colophon's own copy; nothing here composes copy for them.
 *
 * That is also why the composition below is `createElement` rather than JSX. Three calls naming
 * three existing components read unmistakably as "render these"; a JSX tree in this file would
 * read as markup, and markup in this file is exactly the defect.
 *
 * Never imported statically by a component or page: it pulls `react-dom/server` along with it,
 * so the download flow loads it on demand.
 */

/**
 * The one entry point, and the only part of this module that touches the network.
 *
 * Resolving the brand logos and the vendored font subsets to `data:` URIs and fetching the served
 * stylesheets is I/O, so this is `async` — but ALL of it happens here, on the edge, in parallel,
 * before anything is built. Everything past the one await is `buildImpactReportDocumentHtml`,
 * which is pure.
 *
 * `signal` is the DOWNLOAD's own signal, passed straight through to every asset fetch so this leg
 * is cancellable: it runs before a conversion job exists, so nothing downstream can bound it.
 */
export async function serialiseImpactReportDocument(
  source: ImpactReportDocumentSource,
  signal?: AbortSignal,
): Promise<string> {
  const [brand, fonts, styles] = await Promise.all([
    resolveImpactReportBrandAssets(signal),
    resolveImpactReportDocumentFonts(signal),
    resolveImpactReportDocumentStyles(signal),
  ]);

  return buildImpactReportDocumentHtml({ ...source, assets: { brand, fonts, styles } });
}

/**
 * The document, built. PURE: same inputs in, byte-identical string out.
 *
 * No clock, no randomness, no generated identifier and no DOM — `renderToStaticMarkup` emits no
 * hydration markers, no `data-react*` attributes and no comment separators, so the markup is
 * exactly the elements the components returned. It is exported separately from the async entry
 * point above precisely so this half can be exercised with no browser, no React context and no
 * network at all.
 */
export function buildImpactReportDocumentHtml(input: ImpactReportDocumentInput): string {
  const { pages, dateRange, orgName, photoCredits, suppressCoverPage, assets } = input;
  const documentPages = pages.map(withDocumentBackdrop);

  const markup = renderToStaticMarkup(
    createElement(
      Fragment,
      null,
      createElement(ImpactReportPdfCover, {
        logoSrc: assets.brand.gfgMark,
        orgName,
        dateRangeLabel: formatCoverDateRange(dateRange),
      }),
      createElement(ImpactHeroDocument, {
        pages: documentPages,
        gfgMarkSrc: assets.brand.gfgMark,
        fallbackBackdropSrc: assets.brand.fallbackBackdrop,
        omitDownloadAffordance: true,
      }),
      createElement(ImpactReportPdfBackCover, {
        // The SAME resolver the screen calls, given the SAME suppression decision `pages` was
        // built with. The credits carry the wire's page numbers; reconciling them with a document
        // whose first page was dropped is that function's job and has never been this module's.
        // This module's job is only the sheet IT put in front of the hero pages.
        photoCredits: withDocumentSheetNumbers(
          resolveImpactReportPhotoCredits(photoCredits, suppressCoverPage),
        ),
        certificationBadges: withDocumentBadgeSources(assets.brand.certificationBadges),
      }),
    ),
  );

  assertNoOriginRelativeSources(markup);

  // ONE <style>, in cascade order: the shared design tokens (the SAME file index.css imports, its
  // `@theme` wrapper already rewritten to `:root`), then this document's own geometry, reset and
  // token OVERRIDES — after the shared block, so the overrides win — then the @font-face rules,
  // then the stylesheet itself, verbatim, wrapping its own `@layer components` and `@media print`
  // exactly as the screen loads it.
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${IMPACT_REPORT_MASTHEAD_LABEL}</title>
<style>
${assets.styles.tokensCss}
${IMPACT_REPORT_DOCUMENT_GEOMETRY_CSS}
${buildFontFaceCss(assets.fonts)}
${assets.styles.documentCss}
</style>
</head>
<body>
${markup}
</body>
</html>
`;
}

/**
 * The cover's date line, formatted EXACTLY as `ImpactReportMasthead` formats it on screen: the
 * matched bounds, or the all-time label when both ends are `null` on an empty result. The two
 * must change together, or the PDF dates the report differently from the screen.
 */
function formatCoverDateRange(dateRange: ImpactReportDocumentSource["dateRange"]): string {
  return dateRange.from && dateRange.to
    ? `${formatDateLong(dateRange.from)} – ${formatDateLong(dateRange.to)}`
    : IMPACT_REPORT_ALL_TIME_LABEL;
}

/**
 * The resolved credits, renumbered onto THIS document's sheets.
 *
 * The resolver's numbers describe the hero pages, summary page first, which is exactly what the
 * screen shows. The PDF prints its cover on a sheet of its own ahead of them, so every photograph
 * lands that many sheets later. The shift is the one fact this module owns — the sheets it added —
 * and nothing about it is read off the payload. `undefined` stays `undefined`: the back cover
 * reads that distinction.
 */
function withDocumentSheetNumbers(
  photoCredits: ImpactReportDocumentSource["photoCredits"],
): ImpactReportDocumentSource["photoCredits"] {
  return photoCredits?.map((credit) => ({
    ...credit,
    page: credit.page + IMPACT_REPORT_DOCUMENT_SHEETS_BEFORE_PAGES,
  }));
}

/** `@font-face` rules for the four vendored subsets, each carrying its own bytes. */
function buildFontFaceCss(fonts: ImpactReportFontDataUris): string {
  const { style, display, format } = IMPACT_REPORT_DOCUMENT_FONT_FACE_DESCRIPTORS;

  return IMPACT_REPORT_DOCUMENT_FONT_FACES.map(
    (face) => `@font-face {
  font-family: "${face.family}";
  font-style: ${style};
  font-weight: ${face.weight};
  font-display: ${display};
  src: url("${fonts[face.key]}") format("${format}");
}`,
  ).join("\n");
}

/**
 * The one page-model rewrite this module performs, and it touches a URL only — never a page's
 * copy, its order, its kind or its statistics.
 *
 * The bundled fallback (app-origin-relative, so unreachable from the converter) is NOT swapped
 * here: the hero components read it from `fallbackBackdropSrc`, which the builder passes as the
 * same asset's `data:` URI. A `null` backdrop is EXPLICITLY NO BACKGROUND and stays `null`.
 * CAUSE PHOTOGRAPHY is a remote absolute URL and STAYS one, asked for at the size the page prints
 * it — inlining full-bleed photographs would put megabytes of base64 into a POST body the
 * converter bounds, for images the converter can simply fetch.
 */
function withDocumentBackdrop(page: ImpactReportPage): ImpactReportPage {
  if (page.backdropUrl === null) return page;
  return { ...page, backdropUrl: withBackdropResizeParams(page.backdropUrl) };
}

/**
 * Asks the CDN for the backdrop at the resolution the page actually prints it — see
 * `IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PX` for the placed dimensions and the DPI arithmetic.
 *
 * Anything that is not an absolute `http(s)` URL is returned untouched, which is what keeps this
 * safe to run over every page: the bundled fallback's relative path is resolved by the component,
 * and a relative URL from the wire would be a payload defect this function must not paper over by
 * inventing an origin for it (`assertNoOriginRelativeSources` reports it instead).
 *
 * `searchParams.set` rather than string concatenation: the URL may already carry a query, and a
 * naive `?` would corrupt it. It also makes the output stable — one parameter, in place.
 */
function withBackdropResizeParams(backdropUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(backdropUrl);
  } catch {
    return backdropUrl;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return backdropUrl;

  parsed.searchParams.set(
    IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PARAM,
    String(IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PX),
  );
  return parsed.toString();
}

/**
 * The back cover's badges with each remote `src` swapped for its resolved `data:` URI, in the SAME
 * order the resolver returned them. The `alt` text is the badge's own and is never touched.
 */
function withDocumentBadgeSources(
  badgeDataUris: readonly string[],
): typeof IMPACT_REPORT_CERTIFICATION_BADGES {
  if (badgeDataUris.length !== IMPACT_REPORT_CERTIFICATION_BADGES.length) {
    throw new Error(
      `Impact report document: expected ${IMPACT_REPORT_CERTIFICATION_BADGES.length} resolved certification badges, received ${badgeDataUris.length}`,
    );
  }

  return IMPACT_REPORT_CERTIFICATION_BADGES.map((badge, index) => ({
    ...badge,
    src: badgeDataUris[index] ?? badge.src,
  }));
}

/**
 * The whole point of the asset work, asserted rather than assumed.
 *
 * An app-origin-relative `src` is the failure this document class cannot survive and cannot show:
 * the converter resolves it against its own origin, gets nothing, and renders a page with a
 * missing image — no error, no warning, and a client-facing report with a hole in it. Failing here
 * turns that into a specific, attributable error at the one point that can still do something
 * about it.
 */
function assertNoOriginRelativeSources(markup: string): void {
  if (!markup.includes('src="/')) return;

  throw new Error(
    "Impact report document: the serialised markup still carries an app-origin-relative src, which the PDF converter cannot resolve",
  );
}
